import React, { useState, useRef, useEffect, useCallback } from 'react';
import DemoPlatformDashboard from './DemoPlatformDashboard';

const API = '/api/demo';

// ── Brand colors ─────────────────────────────────────────────────────────────
const BRAND = {
  primary: '#2563eb',
  accent:  '#7c3aed',
  dark:    '#0f172a',
  mid:     '#1e293b',
  text:    '#f1f5f9',
  muted:   '#94a3b8',
  success: '#10b981',
  warn:    '#f59e0b',
  danger:  '#ef4444',
  hot:     '#ef4444',
  warm:    '#f59e0b',
  cold:    '#3b82f6',
};

// ── Voice Personas (8 voices per spec) ───────────────────────────────────────
const VOICES = [
  { id: 'shimmer',    name: 'Sophie',  country: 'UK',          gender: 'Female', accent: 'British English',   latency: '~0.8s', color: '#ec4899', avatar: '👩‍💼', quality: 'Studio',     lang: 'en-GB' },
  { id: 'onyx',      name: 'James',   country: 'UK',          gender: 'Male',   accent: 'British English',   latency: '~0.8s', color: '#0891b2', avatar: '👨‍💼', quality: 'Studio',     lang: 'en-GB' },
  { id: 'nova',      name: 'Aria',    country: 'US',          gender: 'Female', accent: 'American English',  latency: '~0.8s', color: '#7c3aed', avatar: '👩‍🦱', quality: 'Studio',     lang: 'en-US' },
  { id: 'alloy',     name: 'Alex',    country: 'US',          gender: 'Male',   accent: 'American English',  latency: '~0.8s', color: '#2563eb', avatar: '👨‍🦳', quality: 'Studio',     lang: 'en-US' },
  { id: 'fable',     name: 'Emma',    country: 'Australia',   gender: 'Female', accent: 'Australian English',latency: '~0.8s', color: '#059669', avatar: '👩‍🦰', quality: 'Studio',     lang: 'en-AU' },
  { id: 'echo',      name: 'Priya',   country: 'India',       gender: 'Female', accent: 'Indian English',    latency: '~0.8s', color: '#d97706', avatar: '👩‍💻', quality: 'Studio',     lang: 'en-IN' },
  { id: 'el_arabic_f',name: 'Layla',  country: 'UAE',         gender: 'Female', accent: 'Arabic',            latency: '~1.1s', color: '#be185d', avatar: '👩',   quality: 'Neural',     lang: 'ar-AE' },
  { id: 'el_arabic_m',name: 'Omar',   country: 'UAE',         gender: 'Male',   accent: 'Arabic',            latency: '~1.1s', color: '#1d4ed8', avatar: '👨',   quality: 'Neural',     lang: 'ar-AE' },
];

const LANGUAGES = [
  { code: 'en', name: 'English',  flag: '🇬🇧', native: 'English' },
  { code: 'ar', name: 'Arabic',   flag: '🇦🇪', native: 'عربي' },
  { code: 'zh', name: 'Chinese',  flag: '🇨🇳', native: '中文' },
  { code: 'hi', name: 'Hindi',    flag: '🇮🇳', native: 'हिंदी' },
  { code: 'ur', name: 'Urdu',     flag: '🇵🇰', native: 'اردو' },
  { code: 'ru', name: 'Russian',  flag: '🇷🇺', native: 'Русский' },
];

const INDUSTRIES = [
  'Technology', 'Healthcare', 'Finance & Banking', 'Real Estate', 'Retail & E-commerce',
  'Hospitality & Travel', 'Education', 'Legal Services', 'Manufacturing', 'Logistics',
  'Construction', 'Media & Entertainment', 'Non-Profit', 'Government', 'Other',
];
const CALL_VOLUMES = [
  '< 100/month', '100–500/month', '500–2,000/month', '2,000–10,000/month', '> 10,000/month',
];
const PHONE_SYSTEMS = [
  'No current system', 'Traditional PBX', 'Cloud VoIP (RingCentral, 8x8, etc.)',
  'Microsoft Teams', 'Zoom Phone', 'Open Source PBX', 'Avaya / Cisco', 'Other',
];
const USE_CASES = [
  'Customer Support', 'Sales & Lead Qualification', 'Appointment Booking',
  'HR & Recruitment', 'IT Helpdesk', 'Billing & Payments',
  'Technical Support', 'Order Status & Tracking', 'Product Information',
];
const COUNTRIES = [
  'United Arab Emirates', 'United Kingdom', 'United States', 'Saudi Arabia', 'Qatar',
  'Kuwait', 'Bahrain', 'Oman', 'Australia', 'Canada', 'India', 'Germany', 'France', 'Other',
];

// ── Styles helper ─────────────────────────────────────────────────────────────
const S = {
  page: {
    minHeight: '100vh',
    background: `linear-gradient(135deg, ${BRAND.dark} 0%, #1a0533 50%, #0f1f3d 100%)`,
    color: BRAND.text, fontFamily: "'Inter', -apple-system, sans-serif",
  } as React.CSSProperties,

  container: { maxWidth: 960, margin: '0 auto', padding: '0 20px' } as React.CSSProperties,

  card: {
    background: 'rgba(30,41,59,0.85)', backdropFilter: 'blur(20px)',
    border: '1px solid rgba(148,163,184,0.15)', borderRadius: 20,
    padding: '40px', marginBottom: 24,
  } as React.CSSProperties,

  btn: (color = BRAND.primary, full = false) => ({
    background: color, color: '#fff', border: 'none', borderRadius: 12,
    padding: '14px 32px', fontSize: 15, fontWeight: 700, cursor: 'pointer',
    width: full ? '100%' : 'auto', transition: 'all 0.2s', display: 'inline-block',
    letterSpacing: 0.3,
  } as React.CSSProperties),

  input: {
    width: '100%', background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(148,163,184,0.2)',
    borderRadius: 12, padding: '13px 16px', color: BRAND.text, fontSize: 15,
    outline: 'none', boxSizing: 'border-box' as const,
  } as React.CSSProperties,

  label: {
    display: 'block', color: BRAND.muted, fontSize: 12, fontWeight: 600,
    marginBottom: 6, letterSpacing: 0.8, textTransform: 'uppercase' as const,
  } as React.CSSProperties,

  stepBadge: (active: boolean, done: boolean) => ({
    width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, fontSize: 15, flexShrink: 0,
    background: done ? BRAND.success : active ? BRAND.primary : 'rgba(148,163,184,0.15)',
    color: (done || active) ? '#fff' : BRAND.muted,
    transition: 'all 0.3s',
  } as React.CSSProperties),
};

// ── Step indicator ────────────────────────────────────────────────────────────
function StepIndicator({ step }: { step: number }) {
  const steps = ['Your Info', 'Knowledge', 'Voice', 'Deploying', 'Live Demo'];
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, marginBottom: 40 }}>
      {steps.map((label, i) => (
        <React.Fragment key={i}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <div style={S.stepBadge(step === i, step > i)}>
              {step > i ? '✓' : i + 1}
            </div>
            <span style={{ fontSize: 10, color: step >= i ? BRAND.text : BRAND.muted, fontWeight: step === i ? 700 : 400, whiteSpace: 'nowrap' }}>
              {label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div style={{
              flex: 1, height: 2, minWidth: 24,
              background: step > i ? BRAND.success : 'rgba(148,163,184,0.2)',
              margin: '0 6px', marginBottom: 22, transition: 'background 0.3s',
            }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Step 1: Lead Form ─────────────────────────────────────────────────────────
function Step1({ onNext }: { onNext: (data: any) => void }) {
  const [form, setForm] = useState({
    lead_name: '', lead_email: '', lead_company: '', lead_phone: '',
    lead_country: '', lead_website: '', lead_industry: '',
    current_phone_system: '', monthly_call_volume: '', use_case: '', bot_name: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setError('');
    if (!form.lead_name.trim())  return setError('Please enter your name');
    if (!form.lead_email.trim()) return setError('Please enter your email');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.lead_email)) return setError('Please enter a valid email address');
    setLoading(true);
    try {
      const r = await fetch(`${API}/session`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          bot_name: form.bot_name.trim() || 'Aria',
          utm_source: new URLSearchParams(window.location.search).get('utm_source') || undefined,
        }),
      });
      const d = await r.json();
      if (!r.ok) return setError(d.error || 'Failed to create session');
      onNext({ session_id: d.session_id, kb_id: d.kb_id, form });
    } catch {
      setError('Network error — please try again');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={S.card}>
      <h2 style={{
        fontSize: 28, fontWeight: 800, marginBottom: 8,
        background: `linear-gradient(90deg, #fff, ${BRAND.primary})`,
        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
      }}>
        Build Your AI Voice Bot in 2 Minutes
      </h2>
      <p style={{ color: BRAND.muted, marginBottom: 32, fontSize: 15 }}>
        No technical skills required. No credit card. Just fill in your details and watch the magic happen.
      </p>

      {error && <div style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, color: '#fca5a5', fontSize: 14 }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={S.label}>Full Name *</label>
          <input style={S.input} placeholder="Jane Smith" value={form.lead_name} onChange={set('lead_name')} />
        </div>
        <div>
          <label style={S.label}>Work Email *</label>
          <input style={S.input} type="email" placeholder="jane@company.com" value={form.lead_email} onChange={set('lead_email')} />
        </div>
        <div>
          <label style={S.label}>Company Name</label>
          <input style={S.input} placeholder="Acme Corp" value={form.lead_company} onChange={set('lead_company')} />
        </div>
        <div>
          <label style={S.label}>Phone Number</label>
          <input style={S.input} placeholder="+971 50 000 0000" value={form.lead_phone} onChange={set('lead_phone')} />
        </div>
        <div>
          <label style={S.label}>Country</label>
          <select style={{ ...S.input }} value={form.lead_country} onChange={set('lead_country')}>
            <option value="">Select country...</option>
            {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label style={S.label}>Company Website</label>
          <input style={S.input} placeholder="https://yourcompany.com" value={form.lead_website} onChange={set('lead_website')} />
        </div>
        <div>
          <label style={S.label}>Industry</label>
          <select style={{ ...S.input }} value={form.lead_industry} onChange={set('lead_industry')}>
            <option value="">Select industry...</option>
            {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
          </select>
        </div>
        <div>
          <label style={S.label}>Monthly Call Volume</label>
          <select style={{ ...S.input }} value={form.monthly_call_volume} onChange={set('monthly_call_volume')}>
            <option value="">Select volume...</option>
            {CALL_VOLUMES.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div>
          <label style={S.label}>Current Phone System</label>
          <select style={{ ...S.input }} value={form.current_phone_system} onChange={set('current_phone_system')}>
            <option value="">Select system...</option>
            {PHONE_SYSTEMS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label style={S.label}>Primary Use Case</label>
          <select style={{ ...S.input }} value={form.use_case} onChange={set('use_case')}>
            <option value="">Select use case...</option>
            {USE_CASES.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
      </div>

      <div style={{ marginBottom: 28 }}>
        <label style={S.label}>Bot Name (what your AI will be called)</label>
        <input style={S.input} placeholder="e.g. Aria, Alex, Sarah, Maya..." value={form.bot_name} onChange={set('bot_name')} />
      </div>

      <button style={S.btn(BRAND.primary, true)} onClick={submit} disabled={loading}>
        {loading ? 'Setting up your demo...' : 'Continue — Add Your Knowledge →'}
      </button>

      <p style={{ textAlign: 'center', color: BRAND.muted, fontSize: 12, marginTop: 16 }}>
        No credit card required. Your data is used only for this demo session.
      </p>
    </div>
  );
}

// ── Step 2: Knowledge Upload ──────────────────────────────────────────────────
type KbMode = 'doc' | 'website' | 'manual' | null;

interface ManualCard { q: string; a: string; }

function Step2({ sessionId, onNext }: { sessionId: string; onNext: () => void }) {
  const [files, setFiles]         = useState<Array<{ name: string; status: string; error?: string }>>([]);
  const [dragging, setDragging]   = useState(false);
  const [uploading, setUploading] = useState('');
  const [error, setError]         = useState('');
  const [mode, setMode]           = useState<KbMode>(null);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [crawling, setCrawling]   = useState(false);
  const [cards, setCards]         = useState<ManualCard[]>([
    { q: 'What does your company do?',              a: '' },
    { q: 'What are your business hours?',           a: '' },
    { q: 'What is your pricing?',                   a: '' },
    { q: 'How can customers contact you?',          a: '' },
    { q: 'Where are you located?',                  a: '' },
  ]);
  const [submittingManual, setSubmittingManual] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const uploadFile = async (file: File) => {
    setUploading(file.name); setError('');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await fetch(`${API}/session/${sessionId}/knowledge`, { method: 'POST', body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Upload failed');
      setFiles(f => [...f, { name: file.name, status: 'processing' }]);
    } catch (e: any) { setError(e.message); }
    finally { setUploading(''); }
  };

  const pollStatus = useCallback(async () => {
    const r = await fetch(`${API}/session/${sessionId}`);
    const d = await r.json();
    if (d.knowledge_files) {
      setFiles(d.knowledge_files.map((f: any) => ({ name: f.filename, status: f.status, error: f.error_msg })));
    }
  }, [sessionId]);

  useEffect(() => {
    if (files.some(f => f.status === 'processing')) {
      const t = setTimeout(pollStatus, 2500);
      return () => clearTimeout(t);
    }
  }, [files, pollStatus]);

  const submitWebsite = async () => {
    let url = websiteUrl.trim();
    if (!url) return setError('Please enter your website URL');
    if (!/^https?:\/\//i.test(url)) url = 'https://' + url;
    setCrawling(true); setError('');
    try {
      const r = await fetch(`${API}/session/${sessionId}/knowledge/url`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Crawl failed');
      setFiles(f => [...f, { name: url, status: 'processing' }]);
    } catch (e: any) { setError(e.message); }
    finally { setCrawling(false); }
  };

  const submitManual = async () => {
    const filled = cards.filter(c => c.q.trim() && c.a.trim());
    if (filled.length < 2) return setError('Please fill in at least 2 question and answer pairs');
    setSubmittingManual(true); setError('');
    const text = filled.map(c => `Q: ${c.q}\nA: ${c.a}`).join('\n\n');
    try {
      const r = await fetch(`${API}/session/${sessionId}/knowledge/faq`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, label: 'Manual Q&A' }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Submit failed');
      setFiles(f => [...f, { name: 'Manual Q&A', status: 'processing' }]);
    } catch (e: any) { setError(e.message); }
    finally { setSubmittingManual(false); }
  };

  const updateCard = (i: number, field: 'q' | 'a', val: string) => {
    setCards(c => c.map((card, idx) => idx === i ? { ...card, [field]: val } : card));
  };

  const hasKnowledge = files.some(f => f.status === 'ready');
  const isProcessing = files.some(f => f.status === 'processing');

  const OptionCard = ({ icon, title, desc, note, onClick, color }: any) => (
    <div
      onClick={onClick}
      style={{
        padding: '28px 20px', borderRadius: 18, cursor: 'pointer', textAlign: 'center',
        border: `2px solid ${color}44`, background: `${color}0d`, transition: 'all 0.2s',
      }}
      onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = `${color}1a`; (e.currentTarget as HTMLDivElement).style.borderColor = color; }}
      onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = `${color}0d`; (e.currentTarget as HTMLDivElement).style.borderColor = `${color}44`; }}
    >
      <div style={{ fontSize: 44, marginBottom: 12 }}>{icon}</div>
      <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 8 }}>{title}</div>
      <div style={{ color: BRAND.muted, fontSize: 13, lineHeight: 1.6, marginBottom: 12 }}>{desc}</div>
      <div style={{ fontSize: 11, color: color, fontWeight: 600 }}>{note}</div>
      <div style={{ marginTop: 14 }}>
        <span style={{ background: color, color: '#fff', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 700 }}>
          Select →
        </span>
      </div>
    </div>
  );

  if (mode === null) {
    return (
      <div style={S.card}>
        <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>Train Your AI</h2>
        <p style={{ color: BRAND.muted, marginBottom: 28, fontSize: 15 }}>
          Choose how to give your AI knowledge about your business. It will use this to answer customer questions accurately.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
          <OptionCard
            icon="📄" color={BRAND.primary} onClick={() => setMode('doc')}
            title="Upload Document"
            desc="Upload a PDF, Word doc, or text file with your product info, FAQs, or service documentation."
            note="PDF · DOCX · TXT · MD — up to 20MB"
          />
          <OptionCard
            icon="🌐" color={BRAND.accent} onClick={() => setMode('website')}
            title="Scan My Website"
            desc="Enter your website URL and we'll automatically scan your pages to build the AI knowledge base."
            note="Scans up to 8 pages automatically"
          />
          <OptionCard
            icon="✏️" color="#059669" onClick={() => setMode('manual')}
            title="Manual Q&A"
            desc="Fill in 5 question and answer cards covering your most common customer questions."
            note="Quick setup — 2 minutes"
          />
        </div>

        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <button onClick={onNext} style={{ background: 'none', border: 'none', color: BRAND.muted, fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}>
            Skip — use general AI without custom knowledge
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={S.card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button onClick={() => { setMode(null); setFiles([]); setError(''); }}
          style={{ background: 'rgba(148,163,184,0.1)', border: 'none', borderRadius: 8, padding: '6px 12px', color: BRAND.muted, cursor: 'pointer', fontSize: 13 }}>
          ← Back
        </button>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>
            {mode === 'doc' ? '📄 Upload Document' : mode === 'website' ? '🌐 Scan Website' : '✏️ Manual Q&A Cards'}
          </h2>
          <p style={{ color: BRAND.muted, fontSize: 13, margin: '4px 0 0' }}>
            {mode === 'doc' ? 'Upload PDF, DOCX, TXT, or Markdown files (up to 20MB each).' :
             mode === 'website' ? 'We\'ll automatically scan your pages and extract your company knowledge.' :
             'Fill in the answers below — we\'ll turn them into your AI\'s knowledge base.'}
          </p>
        </div>
      </div>

      {error && <div style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, color: '#fca5a5', fontSize: 13 }}>{error}</div>}

      {mode === 'doc' && (
        <div
          style={{
            border: `2px dashed ${dragging ? BRAND.primary : 'rgba(148,163,184,0.25)'}`,
            borderRadius: 16, padding: '48px 24px', textAlign: 'center', cursor: 'pointer',
            background: dragging ? 'rgba(37,99,235,0.05)' : 'transparent', transition: 'all 0.2s',
          }}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) uploadFile(f); }}
          onClick={() => !uploading && fileRef.current?.click()}
        >
          <div style={{ fontSize: 48, marginBottom: 12 }}>{uploading ? '⏳' : '📂'}</div>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>
            {uploading ? `Uploading ${uploading}...` : 'Drop your file here or click to browse'}
          </div>
          <div style={{ color: BRAND.muted, fontSize: 14, marginBottom: 20 }}>
            Supported: PDF, DOCX, DOC, TXT, Markdown — max 20MB
          </div>
          <span style={{ background: BRAND.primary, color: '#fff', borderRadius: 10, padding: '10px 28px', fontSize: 14, fontWeight: 600 }}>
            Choose File
          </span>
          <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt,.md" style={{ display: 'none' }}
            onChange={e => e.target.files?.[0] && uploadFile(e.target.files[0])} />
        </div>
      )}

      {mode === 'website' && (
        <div>
          <label style={S.label}>Your Website URL</label>
          <div style={{ display: 'flex', gap: 10 }}>
            <input
              style={{ ...S.input, flex: 1 }}
              placeholder="https://yourcompany.com"
              value={websiteUrl}
              onChange={e => setWebsiteUrl(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !crawling && submitWebsite()}
              disabled={crawling}
            />
            <button style={{ ...S.btn('#7c3aed'), whiteSpace: 'nowrap', flexShrink: 0 }}
              onClick={submitWebsite} disabled={crawling || !websiteUrl.trim()}>
              {crawling ? '🔍 Scanning...' : '🌐 Scan Now'}
            </button>
          </div>
          {crawling && (
            <div style={{ marginTop: 16, padding: '14px 18px', background: 'rgba(124,58,237,0.1)', borderRadius: 12, border: '1px solid rgba(124,58,237,0.2)', fontSize: 13 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>🔍 Scanning your website...</div>
              <div style={{ color: BRAND.muted }}>Reading pages and building the knowledge base. This takes 20–60 seconds.</div>
            </div>
          )}
          <div style={{ marginTop: 10, color: BRAND.muted, fontSize: 12 }}>
            We scan up to 8 pages: home, about, services, pricing, FAQ, contact, and product pages.
          </div>
        </div>
      )}

      {mode === 'manual' && (
        <div>
          {cards.map((card, i) => (
            <div key={i} style={{ marginBottom: 16, background: 'rgba(15,23,42,0.4)', borderRadius: 14, padding: '16px 18px', border: '1px solid rgba(148,163,184,0.1)' }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: BRAND.muted, marginBottom: 10 }}>Card {i + 1}</div>
              <div style={{ marginBottom: 10 }}>
                <label style={S.label}>Question</label>
                <input style={S.input} placeholder={card.q}
                  value={card.q} onChange={e => updateCard(i, 'q', e.target.value)} />
              </div>
              <div>
                <label style={S.label}>Answer</label>
                <textarea
                  style={{ ...S.input, minHeight: 72, resize: 'vertical', fontFamily: 'inherit' }}
                  placeholder="Enter your answer here..."
                  value={card.a} onChange={e => updateCard(i, 'a', e.target.value as any)}
                />
              </div>
            </div>
          ))}
          <button
            style={{ ...S.btn('#059669', true), marginTop: 4 }}
            onClick={submitManual}
            disabled={submittingManual || cards.filter(c => c.q.trim() && c.a.trim()).length < 2}
          >
            {submittingManual ? 'Processing...' : `Submit ${cards.filter(c => c.q && c.a).length} Q&A Cards →`}
          </button>
        </div>
      )}

      {files.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div style={{ fontWeight: 600, fontSize: 12, color: BRAND.muted, marginBottom: 8, letterSpacing: 0.5 }}>KNOWLEDGE BASE STATUS</div>
          {files.map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'rgba(15,23,42,0.4)', borderRadius: 10, marginBottom: 6 }}>
              <div style={{ fontSize: 18 }}>{f.status === 'ready' ? '✅' : f.status === 'error' ? '❌' : '⏳'}</div>
              <div style={{ flex: 1, fontSize: 14, wordBreak: 'break-all' }}>{f.name}</div>
              <div style={{ fontSize: 12, color: f.status === 'ready' ? BRAND.success : f.status === 'error' ? BRAND.danger : BRAND.warn, fontWeight: 600 }}>
                {f.status === 'ready' ? 'Ready' : f.status === 'error' ? (f.error || 'Error') : 'Processing...'}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: BRAND.muted, fontSize: 13 }}>
          {isProcessing ? (mode === 'website' ? '🔍 Scanning pages...' : '⏳ Processing...') :
           hasKnowledge ? '✅ Knowledge base ready!' : ''}
        </span>
        <button
          style={S.btn(hasKnowledge ? BRAND.primary : 'rgba(148,163,184,0.3)')}
          onClick={onNext}
          disabled={isProcessing}
        >
          {isProcessing ? 'Processing...' : hasKnowledge ? 'Continue →' : 'Skip & Continue →'}
        </button>
      </div>
    </div>
  );
}

// ── Step 3: Voice Selection ───────────────────────────────────────────────────
function Step3({ sessionId, botName, onNext }: { sessionId: string; botName: string; onNext: () => void }) {
  const [selected, setSelected] = useState(VOICES[2]); // Default: Aria (US Female)
  const [language, setLanguage] = useState('en');
  const [loading, setLoading]   = useState(false);

  const save = async () => {
    setLoading(true);
    try {
      await fetch(`${API}/session/${sessionId}/voice`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ voice_id: selected.id, voice_name: selected.name, language }),
      });
      onNext();
    } finally {
      setLoading(false);
    }
  };

  const grouped: Record<string, typeof VOICES> = {};
  VOICES.forEach(v => {
    const g = v.country;
    if (!grouped[g]) grouped[g] = [];
    grouped[g].push(v);
  });

  return (
    <div style={S.card}>
      <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>Choose Your Voice</h2>
      <p style={{ color: BRAND.muted, marginBottom: 28, fontSize: 15 }}>
        Select the voice your AI will use. Each is trained for natural, professional telephone conversations.
      </p>

      {Object.entries(grouped).map(([country, voices]) => (
        <div key={country} style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: BRAND.muted, marginBottom: 12, letterSpacing: 0.8 }}>
            {country === 'UAE' ? '🇦🇪' : country === 'UK' ? '🇬🇧' : country === 'US' ? '🇺🇸' : country === 'Australia' ? '🇦🇺' : country === 'India' ? '🇮🇳' : '🌍'} {country.toUpperCase()}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
            {voices.map(v => (
              <div
                key={v.id}
                onClick={() => setSelected(v)}
                style={{
                  padding: '18px 16px', borderRadius: 14, cursor: 'pointer',
                  border: `2px solid ${selected.id === v.id ? v.color : 'rgba(148,163,184,0.15)'}`,
                  background: selected.id === v.id ? `${v.color}15` : 'rgba(15,23,42,0.4)',
                  transition: 'all 0.2s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <div style={{ fontSize: 28 }}>{v.avatar}</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{v.name}</div>
                    <div style={{ fontSize: 12, color: v.color, fontWeight: 600 }}>{v.gender}</div>
                  </div>
                  {selected.id === v.id && <div style={{ marginLeft: 'auto', color: v.color, fontWeight: 700, fontSize: 16 }}>✓</div>}
                </div>
                <div style={{ fontSize: 12, color: BRAND.muted, lineHeight: 1.5 }}>
                  <div>🗣️ {v.accent}</div>
                  <div>⚡ {v.latency} latency</div>
                  <div>🎙️ {v.quality} quality</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div style={{ padding: '16px 20px', background: 'rgba(37,99,235,0.08)', borderRadius: 12, border: '1px solid rgba(37,99,235,0.2)', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ fontSize: 32 }}>{selected.avatar}</div>
        <div>
          <div style={{ fontWeight: 700 }}>Selected: {selected.name} ({selected.country} {selected.gender})</div>
          <div style={{ fontSize: 13, color: BRAND.muted }}>{selected.accent} • {selected.latency} latency</div>
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Select Language</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 10 }}>
          {LANGUAGES.map(lang => (
            <div
              key={lang.code}
              onClick={() => setLanguage(lang.code)}
              style={{
                padding: '10px 18px', borderRadius: 12, cursor: 'pointer',
                border: `2px solid ${language === lang.code ? BRAND.primary : 'rgba(148,163,184,0.2)'}`,
                background: language === lang.code ? 'rgba(37,99,235,0.15)' : 'rgba(15,23,42,0.4)',
                display: 'flex', alignItems: 'center', gap: 8,
                transition: 'all 0.2s',
              }}
            >
              <span style={{ fontSize: 22 }}>{lang.flag}</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{lang.name}</div>
                <div style={{ fontSize: 11, color: BRAND.muted }}>{lang.native}</div>
              </div>
              {language === lang.code && <span style={{ color: BRAND.primary, marginLeft: 4, fontWeight: 700 }}>✓</span>}
            </div>
          ))}
        </div>
      </div>

      <button style={S.btn(BRAND.primary, true)} onClick={save} disabled={loading}>
        {loading ? 'Saving...' : `Use ${selected.name}'s Voice in ${LANGUAGES.find(l => l.code === language)?.name || 'English'} — Deploy Bot →`}
      </button>
    </div>
  );
}

// ── Step 4: Deployment Animation ──────────────────────────────────────────────
const DEPLOY_STEPS = [
  { icon: '📚', label: 'Creating Knowledge Base', ms: 800 },
  { icon: '🤖', label: 'Creating AI Assistant',    ms: 1200 },
  { icon: '🎙️', label: 'Generating Voice Profile', ms: 900 },
  { icon: '📞', label: 'Allocating Phone Number',  ms: 1000 },
  { icon: '⚡', label: 'Activating Voice Bot',      ms: 700 },
  { icon: '✅', label: 'Ready!',                    ms: 500 },
];

function Step4Deploy({ sessionId, botName, onDone }: { sessionId: string; botName: string; onDone: (data: any) => void }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [done, setDone] = useState(false);
  const [demoData, setDemoData] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let idx = 0;
    const provisionBot = async () => {
      // Animate through steps while provisioning runs in parallel
      const animInterval = setInterval(() => {
        idx++;
        setCurrentStep(idx);
        if (idx >= DEPLOY_STEPS.length - 1) clearInterval(animInterval);
      }, 1100);

      try {
        const r = await fetch(`${API}/session/${sessionId}/provision`, { method: 'POST' });
        const d = await r.json();
        clearInterval(animInterval);
        if (!r.ok) {
          setError(d.error || 'Provisioning failed');
          return;
        }
        setCurrentStep(DEPLOY_STEPS.length - 1);
        setTimeout(() => {
          setDone(true);
          setDemoData(d);
        }, 800);
      } catch (e: any) {
        clearInterval(animInterval);
        setError('Connection error during setup');
      }
    };
    provisionBot();
  }, [sessionId]);

  useEffect(() => {
    if (done && demoData) {
      setTimeout(() => onDone(demoData), 800);
    }
  }, [done, demoData, onDone]);

  return (
    <div style={{ ...S.card, textAlign: 'center' }}>
      <h2 style={{ fontSize: 24, fontWeight: 800, marginBottom: 8 }}>
        {done ? '🎉 Your AI Bot is Ready!' : `Setting Up ${botName || 'Your Bot'}...`}
      </h2>
      <p style={{ color: BRAND.muted, marginBottom: 36, fontSize: 15 }}>
        {done ? 'Launching your live demo now...' : 'This takes about 5 seconds. Please wait.'}
      </p>

      {error && <div style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, color: '#fca5a5', fontSize: 14 }}>{error}</div>}

      <div style={{ maxWidth: 400, margin: '0 auto' }}>
        {DEPLOY_STEPS.map((step, i) => {
          const state = i < currentStep ? 'done' : i === currentStep ? 'active' : 'pending';
          return (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 16,
              padding: '14px 20px', borderRadius: 12, marginBottom: 10,
              background: state === 'done' ? 'rgba(16,185,129,0.1)' :
                          state === 'active' ? 'rgba(37,99,235,0.12)' :
                          'rgba(148,163,184,0.05)',
              border: `1px solid ${state === 'done' ? 'rgba(16,185,129,0.3)' :
                                   state === 'active' ? 'rgba(37,99,235,0.3)' :
                                   'rgba(148,163,184,0.1)'}`,
              transition: 'all 0.4s',
              opacity: state === 'pending' ? 0.4 : 1,
            }}>
              <div style={{ fontSize: 22 }}>
                {state === 'done' ? '✅' : state === 'active' ? <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>{step.icon}</span> : step.icon}
              </div>
              <div style={{ flex: 1, textAlign: 'left', fontWeight: state === 'active' ? 700 : 400, fontSize: 14 }}>
                {step.label}
              </div>
              {state === 'active' && (
                <div style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${BRAND.primary}`, borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
              )}
            </div>
          );
        })}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Step 5: Live Demo (Chat + Call Me) ────────────────────────────────────────
function Step5Demo({
  sessionId, botName, voiceId, systemPrompt, botGreeting, demoNumber, numberCountry,
}: {
  sessionId: string; botName: string; voiceId: string; systemPrompt: string;
  botGreeting: string; demoNumber: string | null; numberCountry: string | null;
}) {
  const [messages, setMessages] = useState<Array<{ role: string; content: string; sources?: string[] }>>([
    { role: 'assistant', content: botGreeting || `Hello! I'm ${botName || 'Aria'}. How can I help you today?` }
  ]);
  const [input, setInput]     = useState('');
  const [loading, setLoading] = useState(false);
  const [score, setScore]     = useState(0);
  const [scoreLabel, setScoreLabel] = useState('Cold');
  const [speaking, setSpeaking]     = useState(false);
  const [callPhone, setCallPhone]   = useState('');
  const [calling, setCalling]       = useState(false);
  const [callStatus, setCallStatus] = useState('');
  const [showCallMe, setShowCallMe] = useState(false);
  const [completed, setCompleted]   = useState(false);
  const [summary, setSummary]       = useState<any>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const speak = useCallback((text: string) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(text);
    // Pick voice based on voiceId preference
    const voices = window.speechSynthesis.getVoices();
    const langMap: Record<string, string> = {
      shimmer: 'en-GB', onyx: 'en-GB', nova: 'en-US', alloy: 'en-US',
      fable: 'en-AU', echo: 'en-IN', el_arabic_f: 'ar', el_arabic_m: 'ar',
    };
    const preferredLang = langMap[voiceId] || 'en-US';
    const match = voices.find(v => v.lang.startsWith(preferredLang));
    if (match) utt.voice = match;
    utt.rate = 0.95; utt.pitch = 1.05;
    utt.onstart  = () => setSpeaking(true);
    utt.onend    = () => setSpeaking(false);
    utt.onerror  = () => setSpeaking(false);
    window.speechSynthesis.speak(utt);
  }, [voiceId]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const msg = input.trim();
    setInput('');
    setMessages(m => [...m, { role: 'user', content: msg }]);
    setLoading(true);
    try {
      const r = await fetch(`${API}/session/${sessionId}/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Error');
      setMessages(m => [...m, { role: 'assistant', content: d.reply, sources: d.sources }]);
      setScore(d.score || 0);
      setScoreLabel(d.score_label || 'Cold');
      setTimeout(() => speak(d.reply), 100);
    } catch (e: any) {
      setMessages(m => [...m, { role: 'assistant', content: 'Sorry, I had trouble processing that. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  const placeCall = async () => {
    if (!callPhone.trim()) return setCallStatus('Please enter your phone number');
    setCalling(true); setCallStatus('');
    try {
      const r = await fetch(`${API}/session/${sessionId}/call-me`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: callPhone }),
      });
      const d = await r.json();
      if (!r.ok) {
        setCallStatus(d.error || 'Call failed');
        if (d.demo_number) setCallStatus(`${d.error} — Call ${d.demo_number} directly instead.`);
      } else {
        setCallStatus(`📞 Calling you now! ${botName || 'Your bot'} will speak when you answer.`);
      }
    } catch (e: any) {
      setCallStatus('Connection error. Please try calling the demo number directly.');
    } finally {
      setCalling(false);
    }
  };

  const completDemo = async () => {
    try {
      const r = await fetch(`${API}/session/${sessionId}/complete`, { method: 'POST' });
      const d = await r.json();
      setScore(d.score || score);
      setScoreLabel(d.score_label || scoreLabel);
      setCompleted(true);
      setSummary(d);
    } catch {}
  };

  const scoreColor = scoreLabel === 'Hot' ? BRAND.hot : scoreLabel === 'Warm' ? BRAND.warn : BRAND.cold;
  const scoreEmoji = scoreLabel === 'Hot' ? '🔥' : scoreLabel === 'Warm' ? '🌡️' : '❄️';

  if (completed && summary) {
    return (
      <div style={S.card}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>{scoreEmoji}</div>
          <h2 style={{ fontSize: 28, fontWeight: 800, marginBottom: 8 }}>Demo Complete!</h2>
          <p style={{ color: BRAND.muted, fontSize: 15 }}>Thank you for trying CallEssential AI Voice Bot.</p>
          <div style={{ display: 'inline-block', background: `${scoreColor}20`, border: `2px solid ${scoreColor}`, borderRadius: 12, padding: '12px 28px', marginTop: 16 }}>
            <span style={{ color: scoreColor, fontWeight: 800, fontSize: 20 }}>{scoreEmoji} {scoreLabel} Lead</span>
            <span style={{ color: BRAND.muted, fontSize: 14, marginLeft: 8 }}>Score: {score}/100</span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 32 }}>
          {[
            { label: 'Messages', value: messages.filter(m => m.role === 'user').length },
            { label: 'Bot', value: botName || 'Aria' },
            { label: 'Lead Score', value: `${score}/100` },
          ].map(s => (
            <div key={s.label} style={{ textAlign: 'center', padding: '20px', background: 'rgba(15,23,42,0.4)', borderRadius: 12 }}>
              <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>{s.value}</div>
              <div style={{ color: BRAND.muted, fontSize: 13 }}>{s.label}</div>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontWeight: 700, marginBottom: 14, fontSize: 16 }}>Conversation Transcript</h3>
          <div style={{ background: 'rgba(15,23,42,0.5)', borderRadius: 12, padding: 16, maxHeight: 240, overflowY: 'auto' }}>
            {messages.map((m, i) => (
              <div key={i} style={{ marginBottom: 10, fontSize: 13 }}>
                <span style={{ fontWeight: 700, color: m.role === 'user' ? BRAND.primary : BRAND.accent }}>
                  {m.role === 'user' ? '👤 You' : `🤖 ${botName}`}:
                </span>
                <span style={{ marginLeft: 8, color: BRAND.text }}>{m.content}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <button
            style={{ ...S.btn(BRAND.primary, true) }}
            onClick={() => window.location.href = `mailto:sales@synergytalk.ae?subject=Upgrade Demo to Production&body=Session: ${sessionId}`}
          >
            🚀 Convert to Production →
          </button>
          <button
            style={{ ...S.btn('rgba(148,163,184,0.2)', true) }}
            onClick={() => window.location.href = '/demo'}
          >
            Try Another Demo
          </button>
        </div>

        <p style={{ textAlign: 'center', color: BRAND.muted, fontSize: 12, marginTop: 16 }}>
          A summary has been sent to your email. Our team will follow up shortly.
        </p>
      </div>
    );
  }

  return (
    <div style={S.card}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 44, height: 44, borderRadius: '50%', background: `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.accent})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
            boxShadow: speaking ? `0 0 20px ${BRAND.primary}88` : 'none', transition: 'box-shadow 0.3s',
          }}>
            🤖
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 17 }}>{botName || 'Aria'}</div>
            <div style={{ fontSize: 12, color: speaking ? BRAND.success : BRAND.muted }}>
              {speaking ? '🔊 Speaking...' : loading ? '💭 Thinking...' : '🟢 Online'}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ background: `${scoreColor}20`, border: `1px solid ${scoreColor}`, borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 700, color: scoreColor }}>
            {scoreEmoji} {scoreLabel} Lead
          </div>
        </div>
      </div>

      {/* Demo Numbers Banner */}
      {demoNumber && (
        <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 12, padding: '14px 18px', marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>📞 Your Demo Number is Active</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ fontFamily: 'monospace', fontSize: 18, fontWeight: 700, color: BRAND.success }}>
              {demoNumber}
            </div>
            <div style={{ fontSize: 13, color: BRAND.muted }}>
              {numberCountry === 'UK' ? '🇬🇧 UK' : numberCountry === 'UAE' ? '🇦🇪 UAE' : numberCountry === 'US' ? '🇺🇸 US' : ''} Number — expires in 30 min
            </div>
          </div>
        </div>
      )}

      {/* Call Me Feature */}
      <div style={{ background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.2)', borderRadius: 12, padding: '14px 18px', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: showCallMe ? 14 : 0 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>📲 Test with Your Own Phone</div>
            <div style={{ fontSize: 12, color: BRAND.muted }}>Your bot calls you directly — no need to dial a number</div>
          </div>
          <button
            onClick={() => setShowCallMe(!showCallMe)}
            style={{ ...S.btn(BRAND.accent), padding: '8px 16px', fontSize: 13 }}
          >
            {showCallMe ? 'Close' : 'Call Me Now →'}
          </button>
        </div>
        {showCallMe && (
          <div>
            <div style={{ display: 'flex', gap: 10 }}>
              <input
                style={{ ...S.input, flex: 1 }}
                placeholder="+971 50 123 4567 or +44 7700 123456"
                value={callPhone}
                onChange={e => setCallPhone(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && placeCall()}
                disabled={calling}
              />
              <button
                style={{ ...S.btn(BRAND.accent), whiteSpace: 'nowrap', flexShrink: 0 }}
                onClick={placeCall}
                disabled={calling || !callPhone.trim()}
              >
                {calling ? '📞 Calling...' : '📞 Call Me'}
              </button>
            </div>
            {callStatus && (
              <div style={{
                marginTop: 10, fontSize: 13, padding: '10px 14px', borderRadius: 8,
                background: callStatus.startsWith('📞') ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
                color: callStatus.startsWith('📞') ? BRAND.success : '#fca5a5',
              }}>
                {callStatus}
              </div>
            )}
            <div style={{ marginTop: 8, fontSize: 12, color: BRAND.muted }}>
              Enter your full international number with country code. We'll call you within 10 seconds.
            </div>
          </div>
        )}
      </div>

      {/* Chat */}
      <div style={{ height: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16, padding: '4px 0' }}>
        {messages.map((m, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div style={{
              maxWidth: '78%', padding: '12px 16px', borderRadius: m.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
              background: m.role === 'user' ? BRAND.primary : 'rgba(30,41,59,0.9)',
              border: m.role === 'user' ? 'none' : '1px solid rgba(148,163,184,0.15)',
              fontSize: 14, lineHeight: 1.6,
            }}>
              {m.content}
              {m.sources && m.sources.length > 0 && (
                <div style={{ marginTop: 6, fontSize: 11, color: 'rgba(148,163,184,0.7)' }}>
                  📚 Source: {m.sources.join(', ')}
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div style={{ padding: '12px 16px', borderRadius: '18px 18px 18px 4px', background: 'rgba(30,41,59,0.9)', border: '1px solid rgba(148,163,184,0.15)', fontSize: 14 }}>
              <span style={{ display: 'inline-block', animation: 'pulse 1.5s ease-in-out infinite' }}>💭 Thinking...</span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{ display: 'flex', gap: 10 }}>
        <input
          style={{ ...S.input, flex: 1 }}
          placeholder={`Ask ${botName || 'the bot'} a question...`}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && sendMessage()}
          disabled={loading}
        />
        <button style={{ ...S.btn(BRAND.primary), padding: '14px 20px' }} onClick={sendMessage} disabled={loading || !input.trim()}>
          Send
        </button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
        <div style={{ fontSize: 12, color: BRAND.muted }}>
          {messages.filter(m => m.role === 'user').length} messages • {speaking ? '🔊 Speaking' : 'Text + Voice mode'}
        </div>
        <button onClick={completDemo} style={{ background: 'none', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 8, padding: '8px 16px', color: BRAND.muted, cursor: 'pointer', fontSize: 13 }}>
          Finish Demo & See Results →
        </button>
      </div>

      <style>{`
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.5; } }
      `}</style>
    </div>
  );
}

// ── Root DemoPage ─────────────────────────────────────────────────────────────
// ── Demo password gate ───────────────────────────────────────────────────────
function DemoGate({ onAuth }: { onAuth: () => void }) {
  const [user, setUser]   = React.useState('');
  const [pass, setPass]   = React.useState('');
  const [err,  setErr]    = React.useState('');
  const [show, setShow]   = React.useState(false);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (user.trim() === 'demo' && pass === 'P@ssw0rd@demo786') {
      sessionStorage.setItem('demo_auth', '1');
      onAuth();
    } else {
      setErr('Invalid username or password.');
      setPass('');
    }
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#0f172a',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Inter','Segoe UI',sans-serif",
    }}>
      <div style={{
        background: '#1e293b', borderRadius: 16, padding: '48px 40px', width: 380,
        boxShadow: '0 25px 60px rgba(0,0,0,0.5)', border: '1px solid #334155',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🤖</div>
          <h2 style={{ color: '#f1f5f9', margin: '0 0 6px', fontSize: 24, fontWeight: 800 }}>
            CallEssential Demo
          </h2>
          <p style={{ color: '#94a3b8', margin: 0, fontSize: 14 }}>
            Enter your demo credentials to continue
          </p>
        </div>

        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ display: 'block', color: '#94a3b8', fontSize: 12, fontWeight: 600,
              letterSpacing: 1, marginBottom: 6, textTransform: 'uppercase' }}>
              Username
            </label>
            <input
              type="text"
              value={user}
              onChange={e => { setUser(e.target.value); setErr(''); }}
              placeholder="demo"
              autoComplete="username"
              required
              style={{
                width: '100%', padding: '10px 14px', background: '#0f172a',
                border: err ? '1px solid #ef4444' : '1px solid #334155',
                borderRadius: 8, color: '#f1f5f9', fontSize: 15, boxSizing: 'border-box',
                outline: 'none',
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', color: '#94a3b8', fontSize: 12, fontWeight: 600,
              letterSpacing: 1, marginBottom: 6, textTransform: 'uppercase' }}>
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={show ? 'text' : 'password'}
                value={pass}
                onChange={e => { setPass(e.target.value); setErr(''); }}
                placeholder="••••••••••••"
                autoComplete="current-password"
                required
                style={{
                  width: '100%', padding: '10px 40px 10px 14px', background: '#0f172a',
                  border: err ? '1px solid #ef4444' : '1px solid #334155',
                  borderRadius: 8, color: '#f1f5f9', fontSize: 15, boxSizing: 'border-box',
                  outline: 'none',
                }}
              />
              <button type="button" onClick={() => setShow(s => !s)}
                style={{
                  position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', color: '#64748b',
                  fontSize: 16, padding: 0,
                }}>
                {show ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          {err && (
            <div style={{ background: '#ef444420', border: '1px solid #ef444440',
              borderRadius: 8, padding: '10px 14px', color: '#ef4444', fontSize: 13 }}>
              {err}
            </div>
          )}

          <button type="submit" style={{
            marginTop: 8, padding: '12px 24px',
            background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
            color: '#fff', border: 'none', borderRadius: 8, fontSize: 15,
            fontWeight: 700, cursor: 'pointer', letterSpacing: 0.5,
          }}>
            Access Demo →
          </button>
        </form>

        <p style={{ textAlign: 'center', color: '#475569', fontSize: 12, marginTop: 24, marginBottom: 0 }}>
          Contact your Synergytalk representative for access.
        </p>
      </div>
    </div>
  );
}

export default function DemoPage() {
  const [step, setStep]       = useState(0);
  const [sessionId, setSessionId] = useState('');
  const [kbId, setKbId]       = useState('');
  const [botName, setBotName] = useState('Aria');
  const [voiceId, setVoiceId] = useState('nova');
  const [demoData, setDemoData] = useState<any>(null);

  const goToStep = (n: number) => setStep(n);



  // Full-screen platform dashboard — renders before the wizard container
  if (step === 4 && demoData) {
    return (
      <DemoPlatformDashboard
        sessionId={sessionId}
        botName={botName}
        botGreeting={demoData.bot_greeting || `Hello! I'm ${botName}. How can I help?`}
        demoNumber={demoData.demo_number || null}
        onBack={() => goToStep(3)}
      />
    );
  }

  return (
    <div style={S.page}>
      <div style={S.container}>
        {/* Header */}
        <div style={{ paddingTop: 48, paddingBottom: 24, textAlign: 'center' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: BRAND.muted, letterSpacing: 2, marginBottom: 12 }}>
            CALLESSENTIAL AI PLATFORM
          </div>
          <h1 style={{
            fontSize: 42, fontWeight: 900, marginBottom: 12,
            background: `linear-gradient(90deg, #fff 0%, ${BRAND.primary} 60%, ${BRAND.accent} 100%)`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>
            AI Voice Bot Demo
          </h1>
          <p style={{ fontSize: 17, color: BRAND.muted, maxWidth: 540, margin: '0 auto' }}>
            Build a production-ready AI receptionist for your business in under 2 minutes. No credit card required.
          </p>
        </div>

        <StepIndicator step={step} />

        {step === 0 && (
          <Step1 onNext={d => {
            setSessionId(d.session_id);
            setKbId(d.kb_id);
            setBotName(d.form.bot_name || 'Aria');
            goToStep(1);
          }} />
        )}

        {step === 1 && (
          <Step2 sessionId={sessionId} onNext={() => goToStep(2)} />
        )}

        {step === 2 && (
          <Step3 sessionId={sessionId} botName={botName} onNext={() => goToStep(3)} />
        )}

        {step === 3 && (
          <Step4Deploy sessionId={sessionId} botName={botName} onDone={(d) => {
            setDemoData(d);
            goToStep(4);
          }} />
        )}

        {step === 4 && demoData && (
          <Step5Demo
            sessionId={sessionId}
            botName={botName}
            voiceId={voiceId}
            systemPrompt={demoData.system_prompt || ''}
            botGreeting={demoData.bot_greeting || `Hello! I'm ${botName}. How can I help?`}
            demoNumber={demoData.demo_number || null}
            numberCountry={demoData.number_country || null}
          />
        )}

        {/* Footer */}
        <div style={{ textAlign: 'center', paddingBottom: 40, color: BRAND.muted, fontSize: 13 }}>
          <div>Powered by <strong>CallEssential</strong> · AI Voice Infrastructure</div>
          <div style={{ marginTop: 6, fontSize: 12 }}>
            <a href="https://www.callessential.cc" style={{ color: BRAND.muted, textDecoration: 'none' }}>callessential.cc</a>
            {' · '}
            <a href="mailto:sales@synergytalk.ae" style={{ color: BRAND.muted, textDecoration: 'none' }}>Contact Sales</a>
          </div>
        </div>
      </div>
    </div>
  );
}
